<template>
  <div v-if="priceCatalog && data" class="h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.code"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('code')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.name')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.name"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('name')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('price-catalog.startDate')"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view'">
            {{ isoDateToFullDate(data.startDate) || '-' }}
          </div>
          <DateTimePicker
            v-else
            v-model="selectedStartDate"
            :form-error="getInputErrors('startDate')"
            :show-time-picker="false"
            show-error-as-tooltip
            @update:modelValue="onDateChange($event, 'from')"
            @validation-change="onDateValidationChange($event, 'from')">
          </DateTimePicker>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('price-catalog.endDate')"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view'">
            {{ isoDateToFullDate(data.endDate) || '-' }}
          </div>
          <DateTimePicker
            v-else
            v-model="selectedEndDate"
            :show-time-picker="false"
            show-error-as-tooltip
            :form-error="getInputErrors('endDate')"
            @validation-change="onDateValidationChange($event, 'to')"
            @update:modelValue="onDateChange($event, 'to')">
          </DateTimePicker>
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('material.company')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCompanies"
            :selected-item="selectedCompany"
            name="companies"
            flat
            :mode="mode"
            clearable
            show-error-as-tooltip
            :error="getInputErrors('company')"
            @update:selected="handleCompaniesSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle
            v-else
            :label="$t('baseObject.isActive')"
            name="isActive"
            v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('price-catalog.includeVat')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck
            v-if="mode == 'view'"
            color="blue"
            :item="data.includeVat"></BaseTableCellCheck>
          <BaseToggle
            v-else
            :label="$t('price-catalog.includeVat')"
            name="includeVat"
            v-model="data.includeVat" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import DateTimePicker from '../common/DateTimePicker.vue';

import { type PriceCatalog } from '@/types/PricingTypes';
import type { SelectDropdownItem } from '@/types/Base';

import {
  isoDateToFullDate,
  datepickerToServerDateFormat,
  isoDateToDatepickerFormat,
  compareDates,
} from '@/utils/date';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { watchOnce } from '@vueuse/core';

const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  priceCatalog: PriceCatalog | undefined;
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
}>();

const data = defineModel<PriceCatalog>();

const { getCompaniesLookup } = useLookupsStore();
const { dropdownCompanies } = storeToRefs(useLookupsStore());

const selectedCompany = ref<SelectDropdownItem | null>(null);

function handleCompaniesSelection(item: SelectDropdownItem) {
  selectedCompany.value = item ? item : null;
  if (data.value) data.value.companyId = item ? (item.value as string) : '';
}

onMounted(async () => {
  await getCompaniesLookup();
  if (data.value?.startDate) {
    selectedStartDate.value = isoDateToDatepickerFormat(data.value.startDate);
  }
  if (data.value?.endDate) {
    selectedEndDate.value = isoDateToDatepickerFormat(data.value.endDate);
  }
});

const isModified = ref<boolean>(false);

watchOnce(
  () => data.value?.companyId,
  (newCompanyId) => {
    const selectedValue = dropdownCompanies.value.find((item) => item.value == newCompanyId);
    if (selectedValue) selectedCompany.value = selectedValue;
    else if (dropdownCompanies.value?.[0]) handleCompaniesSelection(dropdownCompanies.value?.[0]);
  },
  { deep: true }
);

watch(
  () => data.value,
  (newData) => {
    validationModel.value.code = newData?.code || '';
    validationModel.value.name = newData?.name || '';
    validationModel.value.startDate = newData?.startDate || '';
    validationModel.value.endDate = newData?.endDate || '';
    validationModel.value.company = newData?.companyId || '';
    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.priceCatalog);
    isModified.value = valueChanged;
  },
  { deep: true }
);

const validationModel = ref({
  code: data.value?.code || '',
  name: data.value?.name || '',
  startDate: data.value?.startDate || '',
  endDate: data.value?.endDate || '',
  company: data.value?.companyId || '',
});

const rules = computed(() => ({
  code: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  name: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  startDate: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    validDate: helpers.withMessage(
      $t('messages.errors.invalidDateFormat'),
      () => !dateValidationErrors.value['from']
    ),
  },
  endDate: {
    minValue: helpers.withMessage(
      'Expiration date cannot be earlier than starting date.',
      (value: string) => {
        return data.value && data.value.startDate
          ? compareDates(value, data.value.startDate, '>=')
          : true;
      }
    ),
    validDate: helpers.withMessage(
      $t('messages.errors.invalidDateFormat'),
      () => !dateValidationErrors.value['to']
    ),
  },
  company: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(type: 'code' | 'name' | 'startDate' | 'endDate' | 'company') {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].minValue && $v.value[type].minValue.$invalid)
    return $v.value[type].minValue.$message;
  else if ($v.value[type].validDate && $v.value[type].validDate.$invalid)
    return $v.value[type].validDate.$message;
  else return '';
}

const selectedStartDate = ref<string>('');
const selectedEndDate = ref<string>('');

function onDateChange(e: string | undefined, dateType: 'from' | 'to') {
  if (!data.value) return;
  if (dateType == 'from') {
    data.value.startDate = datepickerToServerDateFormat(selectedStartDate.value);
  } else {
    data.value.endDate = datepickerToServerDateFormat(selectedEndDate.value);
    $v.value.endDate.$touch();
  }
}

const dateValidationErrors = ref<Record<string, boolean>>({});
function onDateValidationChange(
  validation: {
    isValid: boolean;
    error: string;
  },
  column: 'from' | 'to'
) {
  dateValidationErrors.value[column] = !validation.isValid;
}
</script>
